import { Outlet, useLocation } from 'react-router-dom';

import CreditsExhaustedModal from '#creditFeature/components/CreditsExhaustedModal/CreditsExhaustedModal';
import { useCreditsExhaustedCheck } from '#creditFeature/hooks/useCreditsExhaustedCheck';
import AddonTrialBanner from '#globalComponents/addons/AddonTrialBanner';
import DemoUpgradeBanner from '#globalComponents/DemoUpgradeBanner/DemoUpgradeBanner';
import FeatureErrorBoundary from '#globalComponents/FeatureErrorBoundary/FeatureErrorBoundary';
import ImpersonationBanner from '#globalComponents/ImpersonationBanner/ImpersonationBanner';
import MobileBlockerScreen from '#globalComponents/MobileBlockerScreen/MobileBlockerScreen';
import PaymentRecoveryBanner from '#globalComponents/PaymentRecoveryBanner/PaymentRecoveryBanner';
import Sidebar from '#globalComponents/Sidebar/Sidebar';
import SubscriptionStatusBanner from '#globalComponents/SubscriptionStatusBanner/SubscriptionStatusBanner';
import useIsMobile from '#globalHooks/useIsMobile';
import { CRM_ADDON } from '#lib/addons/addonConfigs';

const MOBILE_ALLOWED_PATHS = ['/crm/onboarding', '/dashboard'];

/**
 * Everything around the page, rendered exactly once: banners, sidebar and the
 * remaining-viewport frame. Padding, max-width and scrolling belong to each
 * page's <Page> scaffold, never to the shell.
 */
const AppShell = () => {
	const { showModal, handleCloseModal, needsSubscription, limitReached } = useCreditsExhaustedCheck();
	const isMobile = useIsMobile();
	const { pathname } = useLocation();

	if (isMobile && !MOBILE_ALLOWED_PATHS.some((p) => pathname.startsWith(p))) {
		return <MobileBlockerScreen />;
	}

	return (
		<div className="flex flex-col h-screen overflow-hidden">
			{/* Banner precedence (most critical first). Each yields when not applicable. */}
			<ImpersonationBanner />
			<PaymentRecoveryBanner />
			<SubscriptionStatusBanner />
			<AddonTrialBanner addon={CRM_ADDON} />
			<DemoUpgradeBanner />

			<div className="flex flex-grow overflow-hidden gap-4">
				{!isMobile && <Sidebar />}

				{/* Must stay `relative`: absolute descendants would otherwise inflate <html>
				    into a phantom outer scrollbar. */}
				<main className="relative flex-grow min-w-0 overflow-hidden">
					<FeatureErrorBoundary>
						<Outlet />
					</FeatureErrorBoundary>
				</main>
			</div>

			<CreditsExhaustedModal
				isOpen={showModal}
				onClose={handleCloseModal}
				needsSubscription={needsSubscription}
				limitReached={limitReached}
			/>
		</div>
	);
};

export default AppShell;
